<template>
  <div class="date-picker-calendar">
    <div class="calendar-header">
      <div class="d-flex">
        <bp-select size="sm" :options="months" v-model="month" />
        <bp-select size="sm" :options="years" v-model="year" />
      </div>
      <div class="d-flex gap-1" v-if="isHours">
        <bp-select size="sm" :options="hours" v-model="hour" class="hours" />
        <span>:</span>
        <bp-select
          size="sm"
          :options="minutes"
          v-model="minute"
          class="hours"
        />
      </div>
    </div>
    <div class="d-flex align-items-center justify-content-between mt-5 px-2">
      <div v-if="!isHours && !single" class="d-flex gap-2">
        <snow-button
          v-for="_mode in ['single', 'range']"
          size="sm"
          :key="_mode"
          class="range-button"
          :variant="mode == _mode ? 'gray' : 'borderless'"
          @click="changeMode(_mode)"
        >
          {{ _mode == "single" ? "Dia" : "Período" }}
        </snow-button>
      </div>

      <div class="d-flex align-items-center gap-1">
        <snow-button
          size="sm"
          variant="borderless"
          icon="fa-regular fa-angle-left"
          @click="subtractMonth"
        />
        <span class="fs-12">
          {{ months.find(m => m.id === month).name.slice(0, 3) }}
        </span>
        <snow-button
          size="sm"
          variant="borderless"
          icon="fa-regular fa-angle-right"
          @click="addMonth"
        />
      </div>
    </div>

    <div class="px-2">
      <table class="w-100 mt-6">
        <thead>
          <th
            class="pb-5"
            v-for="(weekDay, i) in ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']"
            :class="
              i === 0 || i === 6 ? 'text-neutral-076' : 'text-neutral-400'
            "
            :key="i"
          >
            {{ weekDay }}
          </th>
        </thead>
        <tbody>
          <tr v-for="(week, i) in calendar" :key="i">
            <td
              v-for="(day, j) in week"
              :key="j"
              :class="getCellClass(day)"
              @mouseover="onHover(day)"
              @mouseleave="hoveredDate = ''"
            >
              <button
                class="functional-1"
                @click="selectDay(day)"
                :disabled="!day.isCurrentMonth"
              >
                {{ day.day }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex gap-2 mt-3 p-2">
      <snow-button
        size="sm"
        text="Limpar"
        @click="clear"
        variant="borderless"
      />
      <snow-button
        size="sm"
        text="Aplicar"
        :disabled="!start || !end"
        @click="apply"
      />
    </div>
  </div>
</template>
<script>
import {
  getArrayOfDaysInMonth,
  getYearRange,
  getHours,
  getMinutes
} from "@/functions/helpers";
import moment from "moment";
const months = [
  { id: 1, name: "Janeiro" },
  { id: 2, name: "Fevereiro" },
  { id: 3, name: "Março" },
  { id: 4, name: "Abril" },
  { id: 5, name: "Maio" },
  { id: 6, name: "Junho" },
  { id: 7, name: "Julho" },
  { id: 8, name: "Agosto" },
  { id: 9, name: "Setembro" },
  { id: 10, name: "Outubro" },
  { id: 11, name: "Novembro" },
  { id: 12, name: "Dezembro" }
];
export default {
  props: {
    value: {
      type: [Array, String],
      default: () => ["", ""]
    },
    isHours: {
      type: Boolean,
      default: false
    },
    single: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const year = new Date().getFullYear();
    const years = [...getYearRange(year + 10, year - 10, -1)]
      .sort()
      .map(y => ({ id: y, name: y }));

    return {
      months,
      years,
      hours: getHours(),
      minutes: getMinutes(),
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      hour: new Date().getHours(),
      minute: new Date().getMinutes(),
      start: "",
      end: "",
      hoveredDate: "",
      mode: "range"
    };
  },
  computed: {
    calendar() {
      let week = 0;
      let day = 0;
      return [...getArrayOfDaysInMonth(this.year, this.month)].reduce(
        (acc, cur) => {
          if (day === 7) {
            week++;
            day = 0;
            acc.push([]);
          }
          acc[week].push(cur);
          day++;
          return acc;
        },
        [[]]
      );
    }
  },
  methods: {
    prepareDate(day) {
      let [month, year] = [this.month, this.year];
      if (!day.isCurrentMonth) {
        if (day.day > 15) {
          month--;
        } else {
          month++;
        }
      }
      return moment(`${year}-${month}-${day.day}`, "YYYY-MM-DD");
    },
    subtractMonth() {
      this.month = this.month === 1 ? 12 : this.month - 1;
      if (this.month === 12) {
        this.year--;
      }
    },
    addMonth() {
      this.month = this.month === 12 ? 1 : this.month + 1;
      if (this.month === 1) {
        this.year++;
      }
    },
    setStartAndEnd() {
      if (this.isHours) {
        const m = moment(
          this.value,
          ["YYYY-MM-DD HH:mm", "DD/MM/YYYY HH:mm"],
          true
        );
        if (m.isValid()) {
          this.start = m.format("YYYY-MM-DD");
          this.end = m.format("YYYY-MM-DD");
          this.month = m.month() + 1;
          this.year = m.year();
          this.hour = m.hour();
          this.minute = m.minute();
        }
        return;
      }
      if (!Array.isArray(this.value)) {
        return;
      }

      if (!this.value[0] || !this.value[1]) return;
      this.start = moment(this.value[0]).format("YYYY-MM-DD");
      this.end = moment(this.value[1]).format("YYYY-MM-DD");
      this.month = moment(this.value[1]).month() + 1;
      this.year = moment(this.value[1]).year();
    },
    setInitialMode() {
      if (this.isHours) {
        this.mode = "single";
        return;
      }
      if (this.single) {
        this.mode = "single";
        return;
      }
      if (
        Array.isArray(this.value) &&
        this.value[0] &&
        this.value[1] &&
        moment(this.value[0]).isSame(this.value[1], "day")
      ) {
        this.mode = "single";
      } else {
        this.mode = "range";
      }
    },
    changeMode(mode) {
      this.mode = mode;
      this.start = "";
      this.end = "";
    },
    selectDay(day) {
      const date = this.prepareDate(day);

      if (this.isHours && date.isBefore(moment(), "day")) {
        return;
      }

      if (this.mode == "single") {
        this.start = date;
        this.end = date;
        return;
      }

      if (this.start && this.end) {
        this.start = date;
        this.end = "";
        return;
      }
      if (!this.start || date.isBefore(this.start)) {
        this.start = date;
      } else {
        this.end = date;
      }
    },
    clear() {
      this.start = "";
      this.end = "";
      this.apply();
    },
    apply() {
      let start = this.start ? moment(this.start).format("YYYY-MM-DD") : "";
      let end = this.end ? moment(this.end).format("YYYY-MM-DD") : "";

      if (this.isHours && start) {
        start = moment(start)
          .set({ hour: this.hour, minute: this.minute })
          .format("YYYY-MM-DD HH:mm");
      }
      if (this.mode === "single") {
        this.$emit("input", start);
        return;
      }

      this.$emit("input", [start, end]);
    },
    onHover(day) {
      if (this.start && !this.end) {
        const date = this.prepareDate(day);
        if (date.isAfter(this.start)) this.hoveredDate = date;
      }
    },

    isPast(day) {
      const date = this.prepareDate(day);
      if (this.isHours) {
        return date.isBefore(moment(), "day");
      }
      return (!day.isCurrentMonth && day.day < 15) || date.isBefore(this.start);
    },
    isBetween(day) {
      const date = this.prepareDate(day);
      const end = this.end || this.hoveredDate;
      if (!this.start || !end) return false;
      return date.isSameOrAfter(this.start) && date.isSameOrBefore(end);
    },
    isSelected(day) {
      const date = this.prepareDate(day);
      return date.isSame(this.start) || date.isSame(this.end);
    },
    isNotCurrentMonth(day) {
      return !day.isCurrentMonth;
    },
    getCellClass(day) {
      return {
        past: this.isPast(day),
        between: this.isBetween(day),
        selected: this.isSelected(day),
        disabled: !day.isCurrentMonth
      };
    }
  },
  mounted() {
    this.setStartAndEnd();
    this.setInitialMode();
  },
  watch: {
    value: {
      deep: true,
      handler() {
        this.setStartAndEnd();
        this.setInitialMode();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.date-picker-calendar {
  .range-button {
    min-width: 88px;

    @media screen and (max-width: 389px) {
      min-width: 60px;
    }
  }

  .calendar-header {
    display: flex;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid var(--dark-10);
    @media screen and (max-width: 400px) {
      flex: 1 !important;
    }

    ::v-deep {
      .bp-select {
        .bp-select__container {
          border-color: transparent !important;
        }
        .bp-select__inner {
          width: 52px;
          padding: 0px 4px !important;
        }
        .bp-select__options {
          scrollbar-width: thin;
          width: 120px;
          transform: translate3d(0px, 36px, 0px) !important;
          background-color: var(--bg-1);

          .bp-select__option-name {
            font-size: 14px !important;
          }
          .bp-select__option-cell:not(.disabled).selected {
            color: var(--dark) !important;
          }
        }
      }
      .bp-select {
        &.hours {
          background: var(--bg-2);
          border-radius: 8px;
          .bp-select__container {
            width: 100%;
          }
          .bp-select__inner {
            width: 16px;
            padding: 0px !important;
          }
        }
      }
    }
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;
    th,
    td {
      height: 32px;
      text-align: center;
      font-weight: normal;
    }
    td:first-child,
    td:last-child {
      button {
        color: var(--dark-20);
      }
    }
    td {
      button {
        width: 100%;
        height: 32px;
        color: var(--dark);
        box-sizing: border-box;
        background-color: unset;
        border: 1px solid transparent;
        border-radius: 12px;
        transition: 0.2s all;
      }

      &:hover button {
        background-color: #9f9ff8;
        color: var(--light);
      }
    }
    td.past {
      button {
        color: var(--dark-20);
      }
    }
    td.between {
      background-color: #9f9ff8;
      button {
        color: var(--light);
      }
    }
    td.selected {
      border-color: transparent;
      button {
        background-color: #9f9ff8;
        color: white;
      }
    }
    td.disabled button {
      color: var(--dark-20);
      cursor: default;
      opacity: 0.5;
    }
  }
}
</style>
